import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { shopName } from "@/i18n/dynamic";
import { MapPin, Navigation, Loader2, X, Crosshair, Map as MapIcon } from "lucide-react";
import LocationPickerMap from "@/components/LocationPickerMap";

/**
 * Self-contained sheet showing the "Nearby Shops" flow.
 * Trigger from anywhere (currently the Header MapPin icon).
 *
 * Props:
 *  open       : boolean
 *  onClose    : () => void
 */
export default function NearbyShopsSheet({ open, onClose }) {
  const { t, lang } = useLang();
  const [nearby, setNearby] = useState(null); // null=idle, []=empty, [...]=results
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showChoice, setShowChoice] = useState(false);
  const [mapPickerOpen, setMapPickerOpen] = useState(false);

  // Auto-trigger location choice the first time the sheet is opened during this session
  useEffect(() => {
    if (open && nearby === null && !loading && !error) {
      setShowChoice(true);
    }
  }, [open]);

  // Lock body scroll while sheet is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const fetchNearbyAt = async (lat, lng) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/shops/nearby?lat=${lat}&lng=${lng}&radius_km=10&limit=20`);
      setNearby(Array.isArray(data) ? data : []);
    } catch {
      setError(t("nearby.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleUseCurrent = () => {
    setShowChoice(false);
    if (!navigator.geolocation) {
      setError(t("nearby.error"));
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => fetchNearbyAt(pos.coords.latitude, pos.coords.longitude),
      () => { setLoading(false); setError(t("nearby.error")); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  };

  const handlePickManually = () => {
    setShowChoice(false);
    setMapPickerOpen(true);
  };

  const handleLocationConfirmed = async ({ lat, lng }) => {
    setMapPickerOpen(false);
    await fetchNearbyAt(lat, lng);
  };

  const triggerLocate = () => {
    setError("");
    setShowChoice(true);
  };

  if (!open && !mapPickerOpen) return null;

  const sheet = (
    <>
      {open && (
        <div
          className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center"
          onClick={onClose}
          data-testid="nearby-shops-sheet"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full sm:max-w-lg sm:mx-4 sm:rounded-[28px] rounded-t-[28px] p-5 pb-safe surface-elevated border border-white/10 relative fade-up max-h-[85vh] flex flex-col overflow-hidden"
            style={{ background: "linear-gradient(180deg, rgba(15,23,42,0.98), rgba(7,11,25,0.98))" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Grabber (mobile) */}
            <div className="sm:hidden mx-auto mb-3 h-1.5 w-12 rounded-full bg-white/20" />

            {/* Close */}
            <button
              type="button"
              onClick={onClose}
              data-testid="nearby-sheet-close"
              className="absolute top-3 end-3 size-8 rounded-full bg-white/5 hover:bg-white/10 text-white/60 hover:text-white flex items-center justify-center transition"
              aria-label="إغلاق"
            >
              <X className="size-4" />
            </button>

            {/* Header */}
            <div className="pb-4 border-b border-white/10">
              <div className="text-xs font-bold text-primary mb-1.5 flex items-center gap-1.5 uppercase tracking-wider">
                <Navigation className="size-3.5" /> GPS
              </div>
              <h3 className="text-xl sm:text-2xl font-black text-white" data-testid="nearby-sheet-title">
                {t("nearby.title")}
              </h3>
              {nearby === null && !loading && !error && !showChoice && (
                <p className="text-sm text-white/60 mt-1.5">{t("nearby.hint")}</p>
              )}
              {loading && (
                <p className="text-sm text-white/60 mt-1.5 flex items-center gap-2">
                  <Loader2 className="size-4 animate-spin" /> {t("nearby.searching")}
                </p>
              )}
              {error && (
                <p className="text-sm text-rose-400 mt-1.5">{error}</p>
              )}
              {nearby !== null && !loading && nearby.length === 0 && (
                <p className="text-sm text-amber-400 mt-1.5">{t("nearby.none")}</p>
              )}
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto py-4 -mx-1 px-1">
              {showChoice ? (
                <div className="space-y-3">
                  <p className="text-sm text-white/60 mb-1">اختر طريقة تحديد الموقع:</p>
                  <button
                    type="button"
                    onClick={handleUseCurrent}
                    data-testid="nearby-sheet-use-current"
                    className="w-full flex items-center gap-4 p-4 rounded-2xl bg-transparent border-2 border-accent/40 hover:border-accent/70 hover:bg-accent/5 transition text-white group"
                  >
                    <div className="size-12 rounded-full bg-accent/15 border border-accent/30 flex items-center justify-center shrink-0 group-hover:bg-accent/25 transition">
                      <Crosshair className="size-5 text-accent" />
                    </div>
                    <div className="flex-1 text-start">
                      <div className="font-bold">استخدم موقعي الحالي</div>
                      <div className="text-xs text-white/50 mt-0.5">دقيق وسريع باستخدام GPS</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={handlePickManually}
                    data-testid="nearby-sheet-pick-manually"
                    className="w-full flex items-center gap-4 p-4 rounded-2xl bg-transparent border-2 border-accent/40 hover:border-accent/70 hover:bg-accent/5 transition text-white group"
                  >
                    <div className="size-12 rounded-full bg-accent/15 border border-accent/30 flex items-center justify-center shrink-0 group-hover:bg-accent/25 transition">
                      <MapIcon className="size-5 text-accent" />
                    </div>
                    <div className="flex-1 text-start">
                      <div className="font-bold">سأحدد بنفسي</div>
                      <div className="text-xs text-white/50 mt-0.5">اسحب الخريطة لتحديد موقع مخصص</div>
                    </div>
                  </button>
                </div>
              ) : nearby && nearby.length > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  {nearby.map((s) => (
                    <Link
                      key={s.id}
                      to={`/shop/${s.id}`}
                      state={{ shop: s }}
                      onClick={onClose}
                      onTouchStart={() => api.get(`/shops/${s.id}/bundle`).catch(() => {})}
                      data-testid={`nearby-sheet-shop-${s.id}`}
                      className="card-lift rounded-2xl overflow-hidden relative border-2 border-emerald-600/40 bg-slate-900/60 hover:border-emerald-500/70 transition"
                    >
                      <div className="absolute top-2 start-2 z-10 flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold shadow">
                        <Navigation className="size-3" />
                        {s.distance_km} {t("nearby.km")}
                      </div>
                      {s.cover_image ? (
                        <img src={s.cover_image} alt={shopName(s, lang)} className="w-full h-24 object-cover" loading="lazy" decoding="async" />
                      ) : (
                        <div className="w-full h-24 bg-gradient-to-br from-emerald-950/50 to-teal-950/40" />
                      )}
                      <div className="p-2.5">
                        <h4 className="font-bold text-sm text-white line-clamp-1">{shopName(s, lang)}</h4>
                        {s.address && (
                          <div className="text-[11px] text-white/50 mt-0.5 line-clamp-1 flex items-center gap-1">
                            <MapPin className="size-3" /> {s.address}
                          </div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              ) : loading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="size-8 text-primary animate-spin" />
                </div>
              ) : (
                <div className="flex items-center justify-center py-10 text-white/40 text-sm">
                  {t("nearby.hint")}
                </div>
              )}
            </div>

            {/* Footer action */}
            {!showChoice && (
              <div className="pt-3 border-t border-white/10">
                <button
                  type="button"
                  onClick={triggerLocate}
                  disabled={loading}
                  data-testid="nearby-sheet-locate-btn"
                  className="w-full h-11 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-sm flex items-center justify-center gap-2 shadow-lg glow-primary-soft transition disabled:opacity-60"
                >
                  {loading ? <Loader2 className="size-4 animate-spin" /> : <MapPin className="size-4" />}
                  {nearby && nearby.length > 0 ? "تحديث الموقع" : t("nearby.cta")}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Fullscreen map picker (rendered outside the sheet backdrop) */}
      {mapPickerOpen && (
        <LocationPickerMap
          onConfirm={handleLocationConfirmed}
          onCancel={() => setMapPickerOpen(false)}
        />
      )}
    </>
  );

  if (typeof document === "undefined") return null;
  return createPortal(sheet, document.body);
}
